import { ChevronDown, Globe2, LockKeyhole, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { recordTable } from "@/components/tables/table-presentation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  CatalogSelectOption,
  ModelFilterType,
  ModelSourceFilter,
  Provider,
  TranslateFn,
} from "../types";
import { toTestId } from "../utils/catalog";

type CatalogToolbarProps = {
  t: TranslateFn;
  isRTL: boolean;
  loading: boolean;
  isAdmin: boolean;
  searchTerm: string;
  provider: string;
  source: ModelSourceFilter;
  type: ModelFilterType;
  providers: Provider[];
  sources: CatalogSelectOption<ModelSourceFilter>[];
  types: CatalogSelectOption<ModelFilterType>[];
  openMenu: boolean;
  onSearchChange: (value: string) => void;
  onProviderChange: (value: string) => void;
  onSourceChange: (value: ModelSourceFilter) => void;
  onTypeChange: (value: ModelFilterType) => void;
  onMenuOpenChange: (open: boolean) => void;
  onOpenModal: (id: string | null, isPublic: boolean) => void;
};

const FILTER_TRIGGER_CLASS =
  "h-9 w-full bg-input text-foreground shadow-xs sm:w-[200px]";

const CatalogToolbar = ({
  t,
  isRTL,
  loading,
  isAdmin,
  searchTerm,
  provider,
  source,
  type,
  providers,
  sources,
  types,
  openMenu,
  onSearchChange,
  onProviderChange,
  onSourceChange,
  onTypeChange,
  onMenuOpenChange,
  onOpenModal,
}: CatalogToolbarProps) => (
  <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
    <div className="relative w-full xl:w-[300px]">
      <Search aria-hidden="true" className={recordTable.searchIcon} />
      <Input
        data-testid="ai-catalog-search-input"
        disabled={loading}
        aria-label={t("aiCatalog.searchPlaceholder")}
        placeholder={t("aiCatalog.searchPlaceholder")}
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        className={recordTable.search}
      />
    </div>

    <div className="flex flex-wrap items-center gap-4 xl:justify-end">
      <Select
        dir={isRTL ? "rtl" : "ltr"}
        disabled={loading}
        value={provider || "all"}
        onValueChange={(value) => onProviderChange(value === "all" ? "" : value)}
      >
        <SelectTrigger
          data-testid="ai-catalog-provider-filter"
          className={FILTER_TRIGGER_CLASS}
          aria-label={t("aiCatalog.allProviders")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem data-testid="ai-catalog-provider-filter-all" value="all">
            {t("aiCatalog.allProviders")}
          </SelectItem>
          {providers.map((item) => (
            <SelectItem
              data-testid={`ai-catalog-provider-filter-option-${toTestId(item.name)}`}
              key={item.id || item.name}
              value={item.name}
            >
              {item.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        dir={isRTL ? "rtl" : "ltr"}
        disabled={loading}
        value={source || "all"}
        onValueChange={(value) =>
          onSourceChange(value === "all" ? "" : (value as ModelSourceFilter))
        }
      >
        <SelectTrigger
          data-testid="ai-catalog-source-filter"
          className={FILTER_TRIGGER_CLASS}
          aria-label={t("aiCatalog.allSources")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem data-testid="ai-catalog-source-filter-all" value="all">
            {t("aiCatalog.allSources")}
          </SelectItem>
          {sources.map((item) => (
            <SelectItem
              data-testid={`ai-catalog-source-filter-option-${item.value}`}
              key={item.value}
              value={item.value}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        dir={isRTL ? "rtl" : "ltr"}
        disabled={loading}
        value={type || "all"}
        onValueChange={(value) =>
          onTypeChange(value === "all" ? "" : (value as ModelFilterType))
        }
      >
        <SelectTrigger
          data-testid="ai-catalog-type-filter"
          className={FILTER_TRIGGER_CLASS}
          aria-label={t("aiCatalog.allTypes")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem data-testid="ai-catalog-type-filter-all" value="all">
            {t("aiCatalog.allTypes")}
          </SelectItem>
          {types.map((item) => (
            <SelectItem
              data-testid={`ai-catalog-type-filter-option-${item.value}`}
              key={item.value}
              value={item.value}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu open={openMenu} onOpenChange={onMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="ai-catalog-register-model-menu-button"
            disabled={loading}
            className="h-9 rounded-lg px-4"
          >
            <Plus aria-hidden="true" className="size-4" />
            {t("aiCatalog.registerModel")}
            <ChevronDown
              aria-hidden="true"
              className={`size-4 transition-transform duration-200 ${
                openMenu ? "rotate-180" : "rotate-0"
              }`}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={isRTL ? "start" : "end"} className="min-w-48">
          {isAdmin ? (
            <DropdownMenuItem
              data-testid="ai-catalog-register-public-model-menu-item"
              onSelect={() => onOpenModal(null, true)}
            >
              <Globe2 className="size-4 text-foreground" />
              <span>{t("aiCatalog.newPublicModel")}</span>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            data-testid="ai-catalog-register-private-model-menu-item"
            onSelect={() => onOpenModal(null, false)}
          >
            <LockKeyhole className="size-4 text-foreground" />
            <span>{t("aiCatalog.newPrivateModel")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

export default CatalogToolbar;
